import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Avatar, Button, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, Divider, Chip,
  InputAdornment
} from '@mui/material';
import {
  LocalHospital, Healing, MedicalServices, HealthAndSafety, Delete, Add,
  History, EventNote, ChevronRight, Edit, Search, Visibility
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Salud = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Dialogs
  const [tratamientoDialog, setTratamientoDialog] = useState(false);
  const [historialDialog, setHistorialDialog] = useState(false);
  const [vacunasDialog, setVacunasDialog] = useState(false);
  const [isEditingTratamiento, setIsEditingTratamiento] = useState(false);

  // Forms
  const [tratamientoForm, setTratamientoForm] = useState({ animal_id: '', tipo: '', medicamento: '', fecha_inicio: new Date().toISOString().split('T')[0], fecha_fin: '', dosis: '', estado: 'activo' });
  const [vacunaForm, setVacunaForm] = useState({ id: null, animal_id: '', enfermedad: '', fecha_aplicacion: new Date().toISOString().split('T')[0], proxima_dosis: '' });

  // Fetch Data
  const { data: animalesData, isLoading: animalsLoading } = useQuery('animales', async () => {
    const res = await axios.get('/api/animales');
    return res.data;
  });
  
  const { data: tratamientosData } = useQuery('tratamientos', async () => {
    const res = await axios.get('/api/tratamientos');
    return res.data;
  });

  const { data: vacunasData } = useQuery('vacunas', async () => {
    const res = await axios.get('/api/vacunas');
    return res.data;
  });

  const animales = Array.isArray(animalesData) ? animalesData : (animalesData?.animales || []);
  const tratamientos = Array.isArray(tratamientosData) ? tratamientosData : (tratamientosData?.tratamientos || []);
  const vacunas = Array.isArray(vacunasData) ? vacunasData : (vacunasData?.vacunas || []);

  // Mutations
  const createTratamientoMutation = useMutation(async (data) => await axios.post('/api/tratamientos', data), {
    onSuccess: () => { queryClient.invalidateQueries('tratamientos'); setTratamientoDialog(false); showMessage('✅ Protocolo iniciado'); }
  });

  const updateTratamientoMutation = useMutation(async (data) => await axios.put(`/api/tratamientos/${data.id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries('tratamientos'); setTratamientoDialog(false); showMessage('✅ Registro actualizado'); }
  });

  const delTratamiento = useMutation(async (id) => await axios.delete(`/api/tratamientos/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('tratamientos'); showMessage('🗑️ Protocolo eliminado'); }
  });

  const createVacunaMutation = useMutation(async (data) => await axios.post('/api/vacunas', data), {
    onSuccess: () => { queryClient.invalidateQueries('vacunas'); setVacunaForm({ id: null, animal_id: '', enfermedad: '', fecha_aplicacion: new Date().toISOString().split('T')[0], proxima_dosis: '' }); showMessage('💉 Vacunación registrada'); }
  });

  const updateVacunaMutation = useMutation(async (data) => await axios.put(`/api/vacunas/${data.id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries('vacunas'); setVacunaForm({ id: null, animal_id: '', enfermedad: '', fecha_aplicacion: new Date().toISOString().split('T')[0], proxima_dosis: '' }); showMessage('✅ Registro de vacuna actualizado'); }
  });

  const delVacuna = useMutation(async (id) => await axios.delete(`/api/vacunas/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('vacunas'); showMessage('🗑️ Registro eliminado'); }
  });

  const handleEditTratamiento = (t) => {
    setTratamientoForm(t);
    setIsEditingTratamiento(true);
    setTratamientoDialog(true);
  };

  const showMessage = (msg) => { setSnackbarMessage(msg); setOpenSnackbar(true); };

  if (animalsLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Monitorizando bioseguridad...</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Situación Sanitaria</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>Gestión de tratamientos, vacunación y bioseguridad.</Typography>
        </Box>
        <Button 
          className="luxury-button luxury-button-primary"
          variant="contained" 
          startIcon={<Add />}
          onClick={() => { setIsEditingTratamiento(false); setTratamientoForm({ animal_id: '', tipo: '', medicamento: '', fecha_inicio: new Date().toISOString().split('T')[0], fecha_fin: '', dosis: '', estado: 'activo' }); setTratamientoDialog(true); }}
        >
          Nuevo Tratamiento
        </Button>
      </Box>
      
      {/* KPIs Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Crotales Sanos', value: animales.filter(a => a.estado_salud === 'sano').length, color: 'var(--emerald-main)', icon: <HealthAndSafety /> },
          { label: 'En Cuarentena', value: animales.filter(a => a.estado_salud === 'cuarentena').length, color: 'var(--alert-gold)', icon: <Healing /> },
          { label: 'En Tratamiento', value: tratamientos.filter(t => t.estado === 'activo').length, color: 'var(--navy-main)', icon: <MedicalServices /> },
          { label: 'Vacunas Registradas', value: vacunas.length, color: 'var(--slate-light)', icon: <LocalHospital /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card className="premium-card">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 48, height: 48 }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={4}>
        <Grid item xs={12} lg={4}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Accesos Directos</Typography>
          <Card className="premium-card">
            <CardContent sx={{ p: 3 }}>
              <Button fullWidth variant="outlined" startIcon={<History />} onClick={() => setHistorialDialog(true)} sx={{ mb: 2, py: 1.5, borderRadius: '12px' }}>Historial Completo</Button>
              <Button fullWidth variant="outlined" startIcon={<EventNote />} onClick={() => setVacunasDialog(true)} sx={{ py: 1.5, borderRadius: '12px' }}>Calendario Vacunas</Button>
              <Divider sx={{ my: 3 }} />
              <Typography variant="caption" sx={{ fontWeight: 800, mb: 2, display: 'block', color: 'var(--slate-light)' }}>PRÓXIMOS EVENTOS</Typography>
              <Box sx={{ p: 2, bgcolor: 'var(--slate-bg)', borderRadius: '12px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Refuerzo Sanitario</Typography>
                <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>Programado en 48h</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Últimos Protocolos Activos</Typography>
          <Card className="premium-card">
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Animal</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Medicamento</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Dosis</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tratamientos.slice(0, 5).map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{animales.find(a => String(a.id) === String(t.animal_id))?.crotal || t.animal_id}</TableCell>
                      <TableCell>{t.medicamento}</TableCell>
                      <TableCell>{t.dosis}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditTratamiento(t)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => { if(window.confirm('¿Eliminar?')) delTratamiento.mutate(t.id)}}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tratamientos.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>Sin protocolos activos.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Tratamiento Dialog */}
      <Dialog open={tratamientoDialog} onClose={() => setTratamientoDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEditingTratamiento ? 'Editar Protocolo' : 'Nuevo Protocolo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Animal</InputLabel>
                <Select value={tratamientoForm.animal_id} label="Animal" onChange={(e) => setTratamientoForm({...tratamientoForm, animal_id: e.target.value})}>
                  {animales.map(a => (<MenuItem key={a.id} value={a.id}>{a.crotal}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Tipo" value={tratamientoForm.tipo} onChange={(e) => setTratamientoForm({...tratamientoForm, tipo: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Medicamento" value={tratamientoForm.medicamento} onChange={(e) => setTratamientoForm({...tratamientoForm, medicamento: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={tratamientoForm.fecha_inicio} onChange={(e) => setTratamientoForm({...tratamientoForm, fecha_inicio: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Fin" InputLabelProps={{ shrink: true }} value={tratamientoForm.fecha_fin} onChange={(e) => setTratamientoForm({...tratamientoForm, fecha_fin: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Dosis" value={tratamientoForm.dosis} onChange={(e) => setTratamientoForm({...tratamientoForm, dosis: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setTratamientoDialog(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => isEditingTratamiento ? updateTratamientoMutation.mutate(tratamientoForm) : createTratamientoMutation.mutate(tratamientoForm)}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Vacunas Dialog */}
      <Dialog open={vacunasDialog} onClose={() => setVacunasDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Calendario de Vacunación</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 4, p: 3, bgcolor: 'var(--slate-bg)', borderRadius: '16px' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>{vacunaForm.id ? 'Editar Entrada' : 'Registrar Nueva Vacuna'}</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <Select value={vacunaForm.animal_id} displayEmpty onChange={(e) => setVacunaForm({...vacunaForm, animal_id: e.target.value})}>
                    <MenuItem value="" disabled>Animal</MenuItem>
                    {animales.map(a => (<MenuItem key={a.id} value={a.id}>{a.crotal}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth size="small" placeholder="Enfermedad" value={vacunaForm.enfermedad} onChange={(e) => setVacunaForm({...vacunaForm, enfermedad: e.target.value})} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth size="small" type="date" value={vacunaForm.fecha_aplicacion} onChange={(e) => setVacunaForm({...vacunaForm, fecha_aplicacion: e.target.value})} /></Grid>
              <Grid item xs={12} sm={3}>
                <Button fullWidth variant="contained" className="luxury-button luxury-button-primary" onClick={() => vacunaForm.id ? updateVacunaMutation.mutate(vacunaForm) : createVacunaMutation.mutate(vacunaForm)}>
                  {vacunaForm.id ? 'Actualizar' : 'Añadir'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Animal</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Vacuna</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vacunas.map(v => (
                  <TableRow key={v.id}>
                    <TableCell>{animales.find(a => String(a.id) === String(v.animal_id))?.crotal || v.animal_id}</TableCell>
                    <TableCell>{v.enfermedad}</TableCell>
                    <TableCell>{v.fecha_aplicacion}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setVacunaForm(v)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => { if(window.confirm('¿Eliminar?')) delVacuna.mutate(v.id)}}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {vacunas.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>Sin vacunas registradas.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}><Button onClick={() => setVacunasDialog(false)}>Cerrar</Button></DialogActions>
      </Dialog>

      {/* Historial Dialog */}
      <Dialog open={historialDialog} onClose={() => setHistorialDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Historial Sanitario Maestro</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Crotal</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tratamiento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Medicamento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tratamientos.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{animales.find(a => String(a.id) === String(t.animal_id))?.crotal || t.animal_id}</TableCell>
                    <TableCell>{t.tipo}</TableCell>
                    <TableCell>{t.medicamento}</TableCell>
                    <TableCell><Chip label={t.estado} size="small" color={t.estado === 'activo' ? 'primary' : 'default'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}><Button onClick={() => setHistorialDialog(false)}>Cerrar Historial</Button></DialogActions>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Salud;
